import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import PriceComparison from '../components/PriceComparison';
import ImageModal from '../components/ImageModal';

const SellingStatus = ({ percentage }) => {
    let status = 'N/A', color = '#6c757d';
    if (percentage === null || isNaN(percentage)) {} 
    else if (percentage >= 80) { status = 'Profit'; color = 'var(--brand-success)'; }
    else if (percentage >= 50) { status = 'Average'; color = 'var(--brand-warning)'; }
    else if (percentage >= 30) { status = 'Poor'; color = '#fd7e14'; }
    else if (percentage >= 1) { status = 'Bad'; color = 'var(--brand-danger)'; }
    else { status = 'Loss'; color = '#343a40'; }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ height: '15px', width: '15px', backgroundColor: color, borderRadius: '50%', border: '1px solid var(--border-primary)' }}></span>
        <span style={{ fontSize: '1rem', fontWeight: '600' }}>{status}</span>
      </div>
    );
};

const CollapsibleSection = ({ title, children, initialOpen = true }) => {
    const [isOpen, setIsOpen] = useState(initialOpen);
    return (
        <div className="collapsible-section">
            <button className="collapsible-header" onClick={() => setIsOpen(!isOpen)}>
                <h3>{title}</h3>
                <span className={`arrow ${isOpen ? 'open' : ''}`}>▼</span>
            </button>
            {isOpen && <div className="collapsible-content">{children}</div>}
        </div>
    );
};

const SpecField = ({ label, children }) => {
    return (
        <div className="spec-field">
            <strong>{label}</strong>
            <div>{children || 'N/A'}</div>
        </div>
    );
};

const FullSpec = ({ user, productId, onClose }) => {
    const [product, setProduct] = useState(null);
    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showComparison, setShowComparison] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    useEffect(() => {
        if (!productId) {
            setError('No Product ID was provided.');
            setLoading(false);
            return;
        }
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const productRef = doc(db, 'products', productId);
                const productSnap = await getDoc(productRef);
                if (!productSnap.exists()) throw new Error("Product not found.");
                
                const productData = { id: productSnap.id, ...productSnap.data() };
                setProduct(productData);

                if (productData.vendorId) {
                    const vendorRef = doc(db, 'contacts', productData.vendorId);
                    const vendorSnap = await getDoc(vendorRef);
                    if (vendorSnap.exists()) {
                        setVendor({ id: vendorSnap.id, ...vendorSnap.data() });
                    }
                }
            } catch (err) {
                console.error("Error fetching full spec data:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [productId]);
    
    const formatDate = (timestamp) => {
        if (!timestamp) return null;
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-GB');
    };

    if (loading) {
        return <div className="full-spec-container"><p>Loading details...</p></div>;
    }
    
    if (error) {
        return <div className="full-spec-container"><p style={{color: 'var(--brand-danger)'}}>{error}</p></div>;
    }
    
    if (showComparison) {
        return (
            <PriceComparison 
                user={user}
                currentProduct={product}
                linkedProductId={product.linkedProductId}
                onClose={() => setShowComparison(false)}
            />
        );
    }

    if (!product) {
        return null;
    }

    return (
        <>
            {isImageModalOpen && product.imageUrl && (
                <ImageModal 
                    imageUrl={product.imageUrl} 
                    onClose={() => setIsImageModalOpen(false)} 
                />
            )}

            <div className="full-spec-container">
                <div className="spec-header">
                    <button className="close-button top-right" onClick={onClose}>&times;</button>
                    <div className="spec-image" onClick={() => product.imageUrl && setIsImageModalOpen(true)} style={{cursor: product.imageUrl ? 'pointer' : 'default'}}>
                        <img 
                            src={product.imageUrl} 
                            alt={product.itemName || "Product image"} 
                        />
                    </div>
                    <div className="spec-title">
                        <h2>{product.itemName}</h2>
                        <span className="spec-product-id">ID: {product.productId}</span>
                    </div>
                </div>
                
                <div className="spec-body">
                    <CollapsibleSection title="Item Details">
                        <div className="spec-field-grid">
                            <SpecField label="Category">{product.category}</SpecField>
                            <SpecField label="Material">{product.material}</SpecField>
                            <SpecField label="Purchase Price">{product.currency} {Number(product.pricePerItem).toFixed(2)}</SpecField>
                            <SpecField label="Purchase Date">{formatDate(product.purchaseDate)}</SpecField>
                            {product.linkedProductId && (
                                <SpecField label="Linked Product ID">
                                    <button className="product-id-link" onClick={() => setShowComparison(true)}>
                                        {product.linkedProductId}
                                    </button>
                                </SpecField>
                            )}
                            <div className="spec-field full-width">
                                <strong>Item Description</strong>
                                <div>{product.itemDescription || 'No description provided.'}</div>
                            </div>
                        </div>
                    </CollapsibleSection>
                    <CollapsibleSection title="Profit Analysis (MYR)">
                        <div className="spec-field-grid">
                            <SpecField label="Total Landing Cost">RM {Number(product.totalLandingCostMYR).toFixed(2)}</SpecField>
                            <SpecField label="Per Unit Cost">RM {Number(product.perUnitCostMYR).toFixed(2)}</SpecField>
                            <SpecField label="Selling Price">RM {Number(product.sellingPriceMYR).toFixed(2)}</SpecField>
                            <SpecField label="Profit">RM {Number(product.profitMYR).toFixed(2)}</SpecField>
                            <SpecField label="Profit Margin">{Number(product.profitPercentage).toFixed(2)}%</SpecField>
                            <SpecField label="Received Date">{formatDate(product.receivedDate)}</SpecField>
                            <div className="spec-field">
                                <strong>Selling Status</strong>
                                <SellingStatus percentage={product.profitPercentage} />
                            </div>
                        </div>
                    </CollapsibleSection>
                    {vendor && (
                        <CollapsibleSection title="Vendor Details" initialOpen={false}>
                            <div className="spec-field-grid">
                                <SpecField label="Company Name">{vendor.companyName}</SpecField>
                                <SpecField label="Contact Number">{vendor.contactNumber}</SpecField>
                                <SpecField label="Address" className="full-width">{vendor.address}</SpecField>
                            </div>
                        </CollapsibleSection>
                    )}
                </div>
            </div>
        </>
    );
};

export default FullSpec;